import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { IconButtonComponent, IconComponent, trackByFactory } from '@mode/capra';
import { ChartTypes } from '@mode/shared/contract-common';

interface MultiStateInfo {
  isAllowed: boolean;
  isEnabled: boolean;
  isHidden: boolean;
}

interface DataProfileInfo {
  isHidden: boolean;
  isEnabled: boolean;
}

@Component({
  standalone: true,
  selector: 'mode-chart-options-menu',
  imports: [CommonModule, IconButtonComponent, IconComponent, MatDividerModule, MatLegacyMenuModule],
  templateUrl: './chart-options-menu.component.html',
  styleUrls: ['./chart-options-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartOptionsMenuComponent implements OnChanges {
  @Input() isShowTitle = true;
  @Input() isShowCaption = true;
  @Input() captionPosition = ChartTypes.ChartCaptionPosition.Top;
  @Input() dataProfileInfo: DataProfileInfo = { isHidden: false, isEnabled: false };
  @Input() granularityInfo: MultiStateInfo = { isAllowed: false, isEnabled: false, isHidden: true };

  @Output() showTitleChange = new EventEmitter<boolean>();
  @Output() showCaptionChange = new EventEmitter<boolean>();
  @Output() captionPositionChange = new EventEmitter<string>();
  @Output() showGranularityChange = new EventEmitter<boolean>();
  @Output() showDataProfileChange = new EventEmitter<boolean>();

  positions = [
    { type: ChartTypes.ChartCaptionPosition.Top, label: 'Display description above' },
    { type: ChartTypes.ChartCaptionPosition.Bottom, label: 'Display description below' },
  ];

  trackByType = trackByFactory<{ type: ChartTypes.ChartCaptionPosition }>(({ type }) => type);

  ngOnChanges() {
    // Default to 'top' if not explicitly 'bottom'. There could be some
    // old captions persisted with 'above' which was never formally released
    // but existed behind a FF.
    this.captionPosition =
      this.captionPosition === ChartTypes.ChartCaptionPosition.Bottom
        ? ChartTypes.ChartCaptionPosition.Bottom
        : ChartTypes.ChartCaptionPosition.Top;
  }

  toggleShowTitle(): void {
    this.isShowTitle = !this.isShowTitle;
    this.showTitleChange.emit(this.isShowTitle);
  }

  toggleShowCaption(): void {
    this.isShowCaption = !this.isShowCaption;
    this.showCaptionChange.emit(this.isShowCaption);
  }

  setPosition(position: ChartTypes.ChartCaptionPosition) {
    this.captionPosition = position;
    this.captionPositionChange.emit(this.captionPosition);
  }

  toggleShowGranularity(): void {
    this.showGranularityChange.emit(!this.granularityInfo.isEnabled);
  }

  toggleShowDataProfile(): void {
    this.showDataProfileChange.emit(!this.dataProfileInfo.isEnabled);
  }
}
